import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { Translation } from '../i18n/translation.service';
import { requestLanguage } from '../i18n/language-storage';
import { AdminAccountApi, type AdminTokenPair } from '../api/admin-account.api';

const AUTH_RETRY_HEADER = 'X-Auth-Retry';

// Auth endpoints never trigger a refresh: login/refresh would loop, logout must not resurrect a session.
const NO_REFRESH_URL_PARTS: readonly string[] = [
  'admin/AdminAccount/login',
  'admin/AdminAccount/refresh',
  'admin/AdminAccount/logout',
];

let refreshInFlight: Promise<AdminTokenPair> | null = null;

function isUnauthorized(err: HttpErrorResponse): boolean {
  if (err.status === 401) {
    return true;
  }
  const e = err.error;
  if (e && typeof e === 'object' && 'statusCode' in e) {
    const code = (e as { statusCode?: number }).statusCode;
    return code === 401;
  }
  return false;
}

function shouldSkipRefresh(req: HttpRequest<unknown>): boolean {
  if (req.headers.has(AUTH_RETRY_HEADER)) {
    return true;
  }
  const url = req.url;
  return NO_REFRESH_URL_PARTS.some((part) => url.includes(part));
}

function refreshSession(api: AdminAccountApi, auth: AuthService, acceptLanguage: string): Promise<AdminTokenPair> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const ctx = auth.getRefreshContext();
      if (!ctx) {
        auth.clear();
        throw new Error('Missing refresh context');
      }
      try {
        const pair = await api.refresh(ctx.userId, ctx.refreshToken, acceptLanguage);
        auth.setSession(pair);
        return pair;
      } catch (err) {
        // Another tab may have refreshed first: the backend then rejects our (rotated-away) refresh token, but storage
        // holds the winner's pair. Adopt it and retry instead of signing out every tab.
        auth.reloadFromStorage();
        const now = auth.getRefreshContext();
        const access = auth.getAccessToken();
        if (now && access && now.refreshToken !== ctx.refreshToken) {
          return { accessToken: access, refreshToken: now.refreshToken };
        }
        throw err;
      }
    })().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

export const adminAuthRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const api = inject(AdminAccountApi);
  const translation = inject(Translation);
  const router = inject(Router);

  const acceptLanguage = requestLanguage(translation.translateService);

  // This interceptor runs outside `authInterceptor`, so `req` carries no bearer yet. Reading the session token
  // now (synchronously, as `authInterceptor` does) tells us which token this request was sent with.
  const sentToken = auth.getAccessToken();

  const retryWith = (access: string) =>
    next(req.clone({ setHeaders: { Authorization: `Bearer ${access}`, [AUTH_RETRY_HEADER]: '1' } }));

  return next(req).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse)) {
        return throwError(() => err);
      }
      if (shouldSkipRefresh(req)) {
        return throwError(() => err);
      }
      const hasRefresh = !!auth.getRefreshContext();
      if (!isUnauthorized(err) || !hasRefresh) {
        if (err.status === 401) {
          auth.clear();
          void router.navigateByUrl('/login');
        }
        return throwError(() => err);
      }
      // The backend honours only the latest access token, so a second refresh would invalidate the token
      // that in-flight retries use. If a refresh already completed since this request was sent (here or in another
      // tab, whose pair is in storage), just retry.
      auth.reloadFromStorage();
      const current = auth.getAccessToken();
      if (current && current !== sentToken) {
        return retryWith(current);
      }
      return from(refreshSession(api, auth, acceptLanguage)).pipe(
        // Only a refresh failure logs out; errors from the retried request below propagate unchanged.
        catchError(() => {
          auth.clear();
          void router.navigateByUrl('/login');
          return throwError(() => err);
        }),
        switchMap(() => {
          const access = auth.getAccessToken();
          if (!access) {
            auth.clear();
            void router.navigateByUrl('/login');
            return throwError(() => err);
          }
          return retryWith(access);
        }),
      );
    }),
  );
};
